import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, Play, Square, PackageMinus, Save, Paperclip, FileText, Upload } from "lucide-react";
import { api, getCurrentUser } from "@/lib/apiClient";
import { toast } from "sonner";
import { MACHINING_STATUS_LABELS, MACHINING_STATUS_COLORS, type MachiningOrder } from "@/types/design";
import { uploadPnFile } from "@/components/Design/PnPhotoUpload";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  order: MachiningOrder | null;
  onChanged?: () => void;
}

interface OpRow {
  id?: string;
  program: string; tool_code: string; tool_offset: string; holder_code: string; tool_number: string; origin_number: string; duration: string; observations: string;
}
const emptyRow = (): OpRow => ({ program: "", tool_code: "", tool_offset: "", holder_code: "", tool_number: "", origin_number: "", duration: "", observations: "" });

export function MachiningOrderManage({ open, onOpenChange, order, onChanged }: Props) {
  const [rows, setRows] = useState<OpRow[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [tools, setTools] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadMaterials = async (ord: MachiningOrder) => {
    const { data } = await api.from("machining_order_materials").select("*").eq("machining_order_id", ord.id).order("created_at");
    if (data && data.length) { setMaterials(data); return; }
    // Compatibilidad: OT antiguas con material único en la propia orden
    if (ord.material_source && ord.material_source !== "none" && ord.material_id) {
      setMaterials([{ id: null, legacy: true, material_source: ord.material_source, material_id: ord.material_id, material_name: ord.material_name, material_qty: ord.material_qty, material_unit: ord.material_unit, discounted: ord.stock_discounted }]);
    } else setMaterials([]);
  };

  useEffect(() => {
    if (!open || !order) return;
    (async () => {
      const { data: ops } = await api.from("machining_operations").select("*").eq("machining_order_id", order.id).order("sequence");
      setRows((ops || []).map((o: any) => ({
        id: o.id, program: o.program || "", tool_code: o.tool_code || "", tool_offset: o.tool_offset || "",
        holder_code: o.holder_code || "", tool_number: o.tool_number || "", origin_number: o.origin_number || "", duration: o.duration || "", observations: o.observations || "",
      })));
      const { data: att } = await api.from("machining_order_attachments").select("*").eq("machining_order_id", order.id).order("created_at", { ascending: false });
      setAttachments(att || []);
      const { data: tl } = await api.from("machining_tools").select("*").eq("is_active", true).order("name");
      setTools(tl || []);
      await loadMaterials(order);
    })();
  }, [open, order]);

  if (!order) return null;

  const setRow = (i: number, k: keyof OpRow, v: string) => setRows((p) => p.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  // al escribir/seleccionar herramienta, autocompletar tool offset desde la biblioteca
  const onToolChange = (i: number, v: string) => setRows((prev) => prev.map((row, idx) => {
    if (idx !== i) return row;
    const t: any = tools.find((t: any) => t.kind === "tool" && (t.code === v || t.name === v));
    return { ...row, tool_code: v, tool_offset: (t && t.tool_offset_mm != null && !row.tool_offset) ? String(t.tool_offset_mm) : row.tool_offset };
  }));

  const saveOps = async () => {
    setSaving(true);
    try {
      await api.from("machining_operations").delete().eq("machining_order_id", order.id);
      const toInsert = rows
        .filter((r) => r.program || r.tool_code || r.observations || r.duration)
        .map((r, idx) => ({ machining_order_id: order.id, sequence: idx + 1,
          program: r.program || null, tool_code: r.tool_code || null, tool_offset: r.tool_offset || null,
          holder_code: r.holder_code || null, tool_number: r.tool_number || null, origin_number: r.origin_number || null,
          duration: r.duration || null, observations: r.observations || null }));
      if (toInsert.length) {
        const { error } = await api.from("machining_operations").insert(toInsert);
        if (error) throw error;
      }
      toast.success("Secuencia guardada");
      onChanged?.();
    } catch (e: any) { toast.error(e.message || "Error al guardar la secuencia"); }
    finally { setSaving(false); }
  };

  const updateOrder = async (patch: any, msg: string) => {
    setBusy(true);
    try {
      const { error } = await api.from("machining_orders").update(patch).eq("id", order.id);
      if (error) throw error;
      toast.success(msg);
      onChanged?.();
    } catch (e: any) { toast.error(e.message || "Error"); }
    finally { setBusy(false); }
  };

  const start = () => updateOrder({ status: "in_progress", started_at: new Date().toISOString() }, "OT iniciada");
  const finish = () => updateOrder({ status: "completed", finished_at: new Date().toISOString() }, "OT finalizada");

  const discountStock = async () => {
    const pend = materials.filter((m) => !m.discounted && m.material_id && Number(m.material_qty));
    if (!pend.length) { toast.info("No hay materiales pendientes de descontar"); return; }
    setBusy(true);
    try {
      const uid = getCurrentUser()?.id || null;
      for (const m of pend) {
        const qty = Number(m.material_qty);
        if (m.material_source === "raw_materials") {
          const { data } = await api.from("raw_materials").select("quantity").eq("id", m.material_id).limit(1);
          const prev = Number(data?.[0]?.quantity || 0); const next = prev - qty;
          const { error: me } = await api.from("raw_material_movements").insert([{
            raw_material_id: m.material_id, movement_type: "out", quantity: qty, reason: "mecanizado",
            reference_type: "machining_order", reference_id: order.id, performed_by: uid, previous_stock: prev, new_stock: next,
          }]);
          if (me) throw me;
          await api.from("raw_materials").update({ quantity: next }).eq("id", m.material_id);
        } else {
          const { data } = await api.from("material_inventory").select("current_stock").eq("id", m.material_id).limit(1);
          const prev = Number(data?.[0]?.current_stock || 0); const next = prev - qty;
          const { error: me } = await api.from("inventory_movements").insert([{
            material_inventory_id: m.material_id, movement_type: "out", quantity: qty, reason: "mecanizado",
            reference_type: "machining_order", reference_id: order.id, performed_by: uid, previous_stock: prev, new_stock: next,
          }]);
          if (me) throw me;
          await api.from("material_inventory").update({ current_stock: next }).eq("id", m.material_id);
        }
        if (m.id) await api.from("machining_order_materials").update({ discounted: true }).eq("id", m.id);
      }
      await api.from("machining_orders").update({ stock_discounted: true }).eq("id", order.id);
      toast.success("Material(es) descontados del stock (registrado en Trazabilidad)");
      onChanged?.();
      await loadMaterials(order);
    } catch (e: any) { toast.error(e.message || "Error al descontar stock"); }
    finally { setBusy(false); }
  };

  const onUploadAttachment = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadPnFile(file);
      const { error } = await api.from("machining_order_attachments").insert([{
        machining_order_id: order.id, file_name: file.name, file_url: url, file_type: file.type, uploaded_by: getCurrentUser()?.id || null,
      }]);
      if (error) throw error;
      toast.success("Adjunto subido");
      const { data } = await api.from("machining_order_attachments").select("*").eq("machining_order_id", order.id).order("created_at", { ascending: false });
      setAttachments(data || []);
    } catch (err: any) { toast.error(err.message || "Error al subir el adjunto"); }
    finally { setUploading(false); if (e.target) e.target.value = ""; }
  };

  const deleteAttachment = async (id: string) => {
    const { error } = await api.from("machining_order_attachments").delete().eq("id", id);
    if (error) { toast.error(error.message); return; }
    setAttachments((p) => p.filter((a) => a.id !== id));
  };

  const allDiscounted = materials.length > 0 && materials.every((m) => m.discounted);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            OT Mecanizado {order.code || ""}
            <Badge className={MACHINING_STATUS_COLORS[order.status]}>{MACHINING_STATUS_LABELS[order.status]}</Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Resumen + acciones */}
        <div className="flex flex-wrap items-center gap-2 justify-between border rounded-lg p-3">
          <div className="text-sm text-muted-foreground">
            {materials.length ? <>Materiales: <b>{materials.length}</b></> : "Sin materiales"}
            {allDiscounted && <Badge variant="outline" className="ml-2 text-green-600 border-green-500/30">Stock descontado</Badge>}
          </div>
          <div className="flex gap-2">
            {order.status === "pending" && <Button size="sm" onClick={start} disabled={busy} className="gap-1 bg-blue-600 hover:bg-blue-700"><Play className="w-4 h-4" />Iniciar</Button>}
            {order.status === "in_progress" && <Button size="sm" onClick={finish} disabled={busy} className="gap-1 bg-green-600 hover:bg-green-700"><Square className="w-4 h-4" />Finalizar</Button>}
            <Button size="sm" variant="outline" onClick={discountStock} disabled={busy || allDiscounted || materials.length === 0} className="gap-1">
              <PackageMinus className="w-4 h-4" />Descontar material
            </Button>
          </div>
        </div>

        {/* Lista de materiales */}
        {materials.length > 0 && (
          <div className="border rounded-lg p-3">
            <h3 className="font-semibold text-sm mb-2">Materiales de la OT</h3>
            <div className="space-y-1">
              {materials.map((m, i) => (
                <div key={m.id || i} className="flex items-center justify-between text-sm">
                  <span>{m.material_name || "Material"} · {m.material_qty} {m.material_unit || ""} <span className="text-xs text-muted-foreground">({m.material_source === "raw_materials" ? "Materia prima" : "Preimpregnado"})</span></span>
                  {m.discounted ? <Badge variant="outline" className="text-green-600 border-green-500/30 text-xs">Descontado</Badge> : <Badge variant="outline" className="text-amber-600 border-amber-500/30 text-xs">Pendiente</Badge>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Adjuntos */}
        <div className="border rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm flex items-center gap-2"><Paperclip className="w-4 h-4" />Adjuntos (imágenes y planos)</h3>
            <label>
              <input type="file" accept=".jpg,.jpeg,.png,.gif,.webp,.pdf" className="hidden" onChange={onUploadAttachment} />
              <Button asChild size="sm" variant="outline" className="gap-1" disabled={uploading}>
                <span>{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Subir</span>
              </Button>
            </label>
          </div>
          {attachments.length === 0 ? <p className="text-xs text-muted-foreground">Sin adjuntos.</p> : (
            <div className="flex flex-wrap gap-3">
              {attachments.map((a) => (
                <div key={a.id} className="border rounded-lg p-2 flex items-center gap-2 text-xs">
                  {/\.pdf($|\?)/i.test(a.file_url) || (a.file_type || "").includes("pdf")
                    ? <a href={a.file_url} target="_blank" rel="noreferrer" className="w-10 h-10 rounded border flex items-center justify-center text-primary bg-primary/5"><FileText className="w-5 h-5" /></a>
                    : <a href={a.file_url} target="_blank" rel="noreferrer"><img src={a.file_url} alt="" className="w-10 h-10 rounded object-cover border" /></a>}
                  <span className="max-w-[140px] truncate">{a.file_name || "archivo"}</span>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => deleteAttachment(a.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Editor de secuencia de operaciones */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Secuencia de operaciones (CNC)</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setRows([...rows, emptyRow()])} className="gap-1"><Plus className="w-4 h-4" />Fila</Button>
              <Button size="sm" onClick={saveOps} disabled={saving} className="gap-1"><Save className="w-4 h-4" />{saving ? "Guardando..." : "Guardar secuencia"}</Button>
            </div>
          </div>
          {/* Biblioteca CNC para autocompletar */}
          <datalist id="cnc-tools">
            {tools.filter((t) => t.kind === "tool").map((t) => <option key={t.id} value={t.code || t.name}>{t.name}{t.tool_offset_mm != null ? ` · off ${t.tool_offset_mm}mm` : ""}</option>)}
          </datalist>
          <datalist id="cnc-holders">
            {tools.filter((t) => t.kind === "holder").map((t) => <option key={t.id} value={t.code || t.name}>{t.name}</option>)}
          </datalist>
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-1 w-6">#</th>
                  <th className="p-1 text-left">Programa</th>
                  <th className="p-1 text-left">Herramienta</th>
                  <th className="p-1 text-left">Tool offset (mm)</th>
                  <th className="p-1 text-left">Porta-herr.</th>
                  <th className="p-1 text-left">Nº herr.</th>
                  <th className="p-1 text-left">Nº origen</th>
                  <th className="p-1 text-left">Duración</th>
                  <th className="p-1 text-left">Observaciones</th>
                  <th className="p-1 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={10} className="p-3 text-center text-muted-foreground">Sin operaciones. Pulsa "Fila" para añadir.</td></tr>}
                {rows.map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-1 text-center text-muted-foreground">{i + 1}</td>
                    <td className="p-1"><Input value={r.program} onChange={(e) => setRow(i, "program", e.target.value)} className="h-7 text-xs" /></td>
                    <td className="p-1"><Input list="cnc-tools" value={r.tool_code} onChange={(e) => onToolChange(i, e.target.value)} className="h-7 text-xs" placeholder="MGM_RoundMill_10" /></td>
                    <td className="p-1"><Input value={r.tool_offset} onChange={(e) => setRow(i, "tool_offset", e.target.value)} className="h-7 text-xs" /></td>
                    <td className="p-1"><Input list="cnc-holders" value={r.holder_code} onChange={(e) => setRow(i, "holder_code", e.target.value)} className="h-7 text-xs" placeholder="MGM_H-02" /></td>
                    <td className="p-1"><Input value={r.tool_number} onChange={(e) => setRow(i, "tool_number", e.target.value)} className="h-7 text-xs" /></td>
                    <td className="p-1"><Input value={r.origin_number} onChange={(e) => setRow(i, "origin_number", e.target.value)} className="h-7 text-xs" /></td>
                    <td className="p-1"><Input value={r.duration} onChange={(e) => setRow(i, "duration", e.target.value)} className="h-7 text-xs" placeholder=">50" /></td>
                    <td className="p-1"><Input value={r.observations} onChange={(e) => setRow(i, "observations", e.target.value)} className="h-7 text-xs" /></td>
                    <td className="p-1 text-center">
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => setRows(rows.filter((_, idx) => idx !== i))}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
